import { useEffect, useState, useMemo } from 'react';
import { selector } from 'selectors/request';
import { LOAD_FLAGS, loadFlags } from 'model/flags';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'redux-orm';
import { orm } from 'model';

// Create a selector to get flag by flagName
const makeFlagSelector = () =>
  createSelector(
    orm,
    (state, flagName) => flagName,
    (session, flagName) => {
      if (!session || !session.Flags) {
        return null;
      }
      const flag = session.Flags.filter(
        (flag) => flag.flagName === flagName
      ).first();
      return flag ? flag.ref : null;
    }
  );

const useFlags = (flagName) => {
  const dispatch = useDispatch();

  // Use flagName as the request ID to differentiate between different flag requests
  const getFlagsSelector = useMemo(
    () => (state) => selector(state, LOAD_FLAGS, flagName),
    [flagName]
  );

  // Create a selector for this specific flag
  const flagSelector = useMemo(makeFlagSelector, []);
  const flagData = useSelector((state) => flagSelector(state, flagName));

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [completed, failed, requestData] = useSelector(getFlagsSelector);

  useEffect(() => {
    if (!flagData && !completed && !failed) {
      dispatch(loadFlags({ flagName, id: flagName }, null, { flagName }));
    }
  }, [dispatch, flagName, flagData, completed, failed]);

  useEffect(() => {
    if (completed) {
      setData(flagData);
      setLoading(false);
      setError(null);
    }

    if (failed) {
      setData(null);
      setLoading(false);
      setError(requestData);
    }
  }, [completed, failed, requestData, flagData]);

  return { data, loading, error };
};

export default useFlags;
